import React, { useState, useEffect } from 'react';
import { Sun, Moon, Menu, X, Code2, Download } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';

interface HeaderProps {
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}

export const Header: React.FC<HeaderProps> = ({ theme, toggleTheme }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Skills', href: '#skills' },
    { name: 'Experience', href: '#experience' },
    { name: 'Work', href: '#work' },
    { name: 'Education', href: '#education' },
    { name: 'Contact', href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);

      const sectionIds = ['home', 'about', 'services', 'skills', 'experience', 'work', 'education', 'contact'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sectionIds) {
        const el = document.getElementById(section);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container header-container">
        <a href="#home" className="logo">
          <div className="logo-icon">
            <Code2 size={22} className="logo-svg" />
          </div>
          <span className="logo-text">
            {portfolioData.name}<span className="text-gradient">{portfolioData.lastName}</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="desktop-nav">
          <ul>
            {navLinks.map((link) => (
              <li key={link.name}>
                <a
                  href={link.href}
                  className={activeSection === link.href.substring(1) ? 'active' : ''}
                >
                  {link.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="header-actions">
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <a href={portfolioData.resumeUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary nav-cv-btn">
            <Download size={16} />
            <span>Resume</span>
          </a>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Navigation"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-drawer">
          <ul>
            {navLinks.map((link) => (
              <li key={link.name}>
                <a
                  href={link.href}
                  className={activeSection === link.href.substring(1) ? 'active' : ''}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              </li>
            ))}
            <li className="mobile-cv-li">
              <a
                href={portfolioData.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Download size={16} /> Download CV
              </a>
            </li>
          </ul>
        </div>
      )}

      <style>{`
        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          padding: 1.25rem 0;
          transition: all 0.3s ease;
        }

        .header.scrolled {
          padding: 0.75rem 0;
          background: var(--bg-glass);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-bottom: 1px solid var(--border-color);
          box-shadow: var(--shadow-sm);
        }

        .header-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
          font-weight: 800;
          font-size: 1.35rem;
          color: var(--text-main);
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-sm);
          background: var(--gradient-brand);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          box-shadow: var(--shadow-glow);
        }

        .desktop-nav ul {
          display: flex;
          align-items: center;
          gap: 2rem;
          list-style: none;
        }

        .desktop-nav a {
          text-decoration: none;
          color: var(--text-muted);
          font-weight: 500;
          font-size: 0.95rem;
          transition: color 0.2s ease;
          position: relative;
          padding: 0.25rem 0;
        }

        .desktop-nav a:hover,
        .desktop-nav a.active {
          color: var(--accent-primary);
        }

        .desktop-nav a.active::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          right: 0;
          height: 2px;
          border-radius: 2px;
          background: var(--gradient-brand);
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .theme-toggle-btn {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-main);
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .theme-toggle-btn:hover {
          color: var(--accent-primary);
          border-color: var(--border-highlight);
          transform: rotate(15deg);
        }

        .nav-cv-btn {
          padding: 0.6rem 1.25rem;
          font-size: 0.88rem;
        }

        .mobile-menu-btn {
          display: none;
          background: transparent;
          border: none;
          color: var(--text-main);
          cursor: pointer;
        }

        .mobile-drawer {
          position: fixed;
          top: 70px;
          left: 0;
          right: 0;
          background: var(--bg-glass);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--border-color);
          padding: 1.5rem;
          box-shadow: var(--shadow-lg);
        }

        .mobile-drawer ul {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .mobile-drawer a {
          text-decoration: none;
          color: var(--text-main);
          font-size: 1.1rem;
          font-weight: 600;
          display: block;
        }

        .mobile-drawer a.active {
          color: var(--accent-primary);
        }

        .mobile-cv-li {
          margin-top: 1rem;
        }

        @media (max-width: 992px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: block;
          }
          .nav-cv-btn {
            display: none;
          }
        }
      `}</style>
    </header>
  );
};
